import type {
  SidebarDock,
  SidebarLayout,
  SidebarPanel,
  SidebarSlotId,
} from "./sidebar-layout-types.ts";

export const SIDEBAR_MIN_WIDTH_PX = 260;
export const SIDEBAR_MIN_HEIGHT_PX = 220;
const SIDEBAR_MAX_WIDTH_PX = 1_200;
const SIDEBAR_MAX_HEIGHT_PX = 800;
const SIDEBAR_MAIN_MIN_WIDTH_PX = 312;
export const SIDEBAR_NARROW_BREAKPOINT_PX = 680;
const SIDEBAR_DIVIDER_WIDTH_PX = 6;

export function clampWidth(width: number): number {
  return Math.min(SIDEBAR_MAX_WIDTH_PX, Math.max(SIDEBAR_MIN_WIDTH_PX, width));
}

export function clampHeight(height: number): number {
  return Math.min(SIDEBAR_MAX_HEIGHT_PX, Math.max(SIDEBAR_MIN_HEIGHT_PX, height));
}

export function sidebarDock(layout: SidebarLayout): SidebarDock {
  return layout.dock === "bottom" || layout.dock === "left" ? layout.dock : "right";
}

export function sidebarMainPanel(layout: SidebarLayout): SidebarPanel | undefined {
  return layout.columns[0]?.panels.find((panel) => panel.id === layout.mainPanelId);
}

export function sidebarSidePanels(layout: SidebarLayout): SidebarPanel[] {
  return layout.columns[0]?.panels.filter((panel) => panel.id !== layout.mainPanelId) ?? [];
}

export function sidebarActivePanel(layout: SidebarLayout): SidebarPanel | undefined {
  return sidebarSidePanels(layout).find((panel) => panel.id === layout.columns[0]?.activePanelId);
}

export function isSidebarSlotVisible(layout: SidebarLayout, slot: SidebarSlotId): boolean {
  if (layout.expanded && layout.expandedSide) {
    return layout.open === true && sidebarActivePanel(layout)?.slot === slot;
  }
  if ((sidebarMainPanel(layout)?.slot ?? "conversation") === slot) {
    return true;
  }
  return layout.open === true && !layout.expanded && sidebarActivePanel(layout)?.slot === slot;
}

export function fitSidebarLayout(
  layout: SidebarLayout,
  availableWidth: number,
): SidebarLayout | null {
  const next = structuredClone(layout);
  if (!Number.isFinite(availableWidth) || availableWidth <= 0) {
    return next;
  }
  const column = next.columns[0];
  if (!column) {
    return next;
  }
  next.columns = [column];
  if (sidebarDock(next) === "bottom") {
    column.height = clampHeight(column.height);
    return next;
  }
  const maxColumnWidth = Math.max(
    SIDEBAR_MIN_WIDTH_PX,
    Math.min(SIDEBAR_MAX_WIDTH_PX, availableWidth * 0.6),
  );
  const budget = Math.max(0, availableWidth - SIDEBAR_MAIN_MIN_WIDTH_PX - SIDEBAR_DIVIDER_WIDTH_PX);
  if (SIDEBAR_MIN_WIDTH_PX > budget) {
    return null;
  }
  column.width = Math.min(maxColumnWidth, budget, clampWidth(column.width));
  return next;
}

export function initializeBrowserSidebarWidth(
  layout: SidebarLayout,
  availableWidth: number,
  chatWidth: number,
): SidebarLayout {
  const column = layout.columns[0];
  if (
    !column?.browserWidthPending ||
    !layout.open ||
    layout.expanded ||
    sidebarDock(layout) === "bottom" ||
    (sidebarMainPanel(layout)?.slot ?? "conversation") !== "conversation" ||
    sidebarActivePanel(layout)?.slot !== "browser" ||
    !Number.isFinite(availableWidth) ||
    availableWidth < SIDEBAR_NARROW_BREAKPOINT_PX ||
    !Number.isFinite(chatWidth) ||
    chatWidth <= 0
  ) {
    return layout;
  }
  const next = structuredClone(layout);
  const nextColumn = next.columns[0]!;
  // Reclaim the centered chat's spare margins; share tighter panes evenly.
  nextColumn.width = Math.max(
    column.width,
    availableWidth - Math.min(chatWidth, availableWidth / 2) - SIDEBAR_DIVIDER_WIDTH_PX,
  );
  delete nextColumn.browserWidthPending;
  return fitSidebarLayout(next, availableWidth) ?? layout;
}
